// Private, tab-local memory. Nothing is written to browser storage or shared
// between accounts; leaving the search workspace discards the whole session.
import { fetchSearchResponse } from './searchTransport';

// Bound both headers and body even when the page supplies only a navigation
// signal. Docket lookups retain their existing longer API deadline.
function fetchSessionResponse(input: string | URL, init?: RequestInit) {
  const timeout = String(input).includes('/search-by-dosar') ? 180_000 : 60_000;
  return fetchSearchResponse(String(input), init || {}, timeout);
}

let owner = '';
let generation = 0;
let lastResultsRoute = '';
const responses = new Map<string, Response>();
const responseMeta = new Map<string, { bytes: number; expires: number }>();
const MAX_CACHE_BYTES = 24 * 1024 * 1024;
const CACHE_TTL_MS = 5 * 60_000;
function storeResponse(key: string, response: Response, bytes: number) {
  responses.delete(key); responseMeta.delete(key);
  if (bytes > MAX_CACHE_BYTES) return;
  responses.set(key, response);
  responseMeta.set(key, { bytes, expires: Date.now() + CACHE_TTL_MS });
  let total = [...responseMeta.values()].reduce((sum, item) => sum + item.bytes, 0);
  while (responses.size > 100 || total > MAX_CACHE_BYTES) {
    const oldest = responses.keys().next().value!;
    total -= responseMeta.get(oldest)?.bytes || 0;
    responses.delete(oldest); responseMeta.delete(oldest);
  }
}
const pending = new Map<string, { promise: Promise<Response>; controller: AbortController; consumers: number }>();
export const searchViews = new Map<string, unknown>();
const routes = new Map<string, string>();
const readOnlyPostPaths = ['/search/overview', '/search/results', '/search/jurisprudence/matters', '/coduri/relevant', '/search-by-dosar/company', '/search-by-dosar'];

function isReadOnlyPost(input: string | URL, init?: RequestInit) {
  const path = String(input).split('?')[0];
  if (readOnlyPostPaths.some(suffix => path.endsWith(suffix))) return true;
  if (!path.endsWith('/search/') || typeof init?.body !== 'string') return false;
  try {
    const body = JSON.parse(init.body);
    // Resuming an existing candidate snapshot does not create a new embedding
    // or spend a search allowance. New factual searches must still reach the API.
    return body.professional_matters === true && typeof body.search_id === 'string' && Boolean(body.search_id);
  } catch { return false; }
}


/** A deliberate submit refreshes data; navigation within a research does not. */
export function refreshSearchSession() {
  generation++;
  for (const entry of pending.values()) entry.controller.abort();
  responses.clear();
  responseMeta.clear();
  pending.clear();
  searchViews.clear();
  routes.clear();
}

export function configureSearchSession(nextOwner: string) {
  if (nextOwner === owner) return;
  const isInitialOwnerSetup = owner === '';
  owner = nextOwner;
  generation++;
  for (const entry of pending.values()) entry.controller.abort();
  responses.clear();
  responseMeta.clear();
  pending.clear();
  searchViews.clear();
  routes.clear();
  lastResultsRoute = '';
  if (!isInitialOwnerSetup) {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem('la_active_ai_nav_session');
      }
    } catch {
      // Ignore storage access errors
    }
  }
}

export function searchRouteKey(path: string, params: URLSearchParams) {
  return JSON.stringify([path, params.get('module') || '', params.get('return_q') || params.get('q') || '', params.get('search_mode') || 'auto', params.get('court_scope') || 'all', params.get('professional_search_id') || params.get('sid') || '', params.get('modules') || '']);
}

export function rememberSearchRoute(path: string, search: string) {
  if (!owner) return;
  lastResultsRoute = path + search;
  if (!new URLSearchParams(search).has('return_q')) return;
  routes.set(searchRouteKey(path, new URLSearchParams(search)), path + search);
}

export function currentSearchResultsRoute() { return owner ? lastResultsRoute : ''; }
export function currentSearchSessionGeneration() { return generation; }

export function resumeSearchRoute(route: string): string {
  const url = new URL(route, 'https://search.local');
  return routes.get(searchRouteKey(url.pathname, url.searchParams)) || route;
}

async function fetchAndCache(input: string | URL, init?: RequestInit): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase();
  const canCache = Boolean(owner) && (method === 'GET' ||
    (method === 'POST' && isReadOnlyPost(input, init)));
  if (!canCache) {
    const epoch = generation;
    const response = await fetchSessionResponse(input, init);
    const bytes = await response.arrayBuffer();
    if (epoch !== generation) throw new DOMException('Search session ended', 'AbortError');
    init?.signal?.throwIfAborted();
    return new Response([204, 205, 304].includes(response.status) ? null : bytes, { status: response.status, statusText: response.statusText, headers: response.headers });
  }
  init?.signal?.throwIfAborted();
  const epoch = generation;
  const makeKey = (url: string, body = init?.body || '') => JSON.stringify([url, method, body, [...new Headers(init?.headers).entries()]]);
  const key = makeKey(String(input));
  const cached = responses.get(key);
  if (cached && (responseMeta.get(key)?.expires || 0) > Date.now()) return cached.clone();
  responses.delete(key); responseMeta.delete(key);
  const response = await fetchSessionResponse(input, init);
  if (epoch !== generation) throw new DOMException('Search session ended', 'AbortError');
  init?.signal?.throwIfAborted();
  if (response.ok) {
    // Consume the clone before caching so an interrupted body is never saved.
    const bytes = await response.clone().arrayBuffer();
    if (epoch !== generation) throw new DOMException('Search session ended', 'AbortError');
    init?.signal?.throwIfAborted();
    if (response.headers.get('content-type')?.includes('json')) {
      const payload = JSON.parse(new TextDecoder().decode(bytes));
      // Do not turn temporary failures or partial pages into permanent session
      // results. A retry must reach the server in these cases.
      if (payload?.success === false || payload?.error || payload?.is_partial || payload?.status === 'incomplete' || payload?.truncated ||
          ['ERROR', 'UNKNOWN'].includes(payload?.iccj_status) || ['ERROR', 'UNKNOWN'].includes(payload?.cedo_status) || payload?.responseHeader?.partialResults ||
          Object.values(payload?.statuses || {}).some(status => status === 'ERROR' || status === 'UNKNOWN')) return response;
    }
    storeResponse(key, new Response(bytes, { status: response.status, statusText: response.statusText, headers: response.headers }), bytes.byteLength);
    const url = new URL(String(input), 'https://search.local');
    const typed = method === 'POST' && url.pathname === '/api/search/results' && typeof init?.body === 'string' ? JSON.parse(init.body) : null;
    const cursor = typed?.cursor || url.searchParams.get('cursor');
    if (cursor && cursor !== '*' && (typed || ['/api/search/advanced', '/api/search/persons'].includes(url.pathname))) {
      const persons = url.pathname.endsWith('/persons');
      if (persons) url.searchParams.delete('cursor');
      else url.searchParams.set('cursor', '*');
      const firstUrl = String(input).startsWith('/') ? url.pathname + url.search : url.href;
      const firstKey = typed ? makeKey(String(input), JSON.stringify({ ...typed, cursor: '*' })) : makeKey(firstUrl);
      const first = responses.get(firstKey);
      if (first) {
        const previous = await first.clone().json();
        const next = await response.clone().json();
        const merged = { ...previous, ...next };
        if (persons) merged.results = [...previous.results, ...next.results];
        else {
          merged.response = { ...next.response, docs: [...previous.response.docs, ...next.response.docs] };
          merged.highlighting = { ...previous.highlighting, ...next.highlighting };
          merged.facets = previous.facets;
        }
        if (epoch === generation) {
          const encoded = new TextEncoder().encode(JSON.stringify(merged));
          storeResponse(firstKey, new Response(encoded, { headers: response.headers }), encoded.byteLength);
        }
      }
    }
  }
  return response;
}

export async function searchSessionFetch(input: string | URL, init?: RequestInit): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase();
  const readOnly = method === 'GET' || (method === 'POST' && isReadOnlyPost(input, init));
  if (!owner || !readOnly) return fetchAndCache(input, init);
  init?.signal?.throwIfAborted();
  const key = JSON.stringify([String(input), method, init?.body || '', [...new Headers(init?.headers).entries()]]);
  let entry = pending.get(key);
  if (!entry || entry.controller.signal.aborted) {
    const controller = new AbortController();
    entry = { promise: fetchAndCache(input, { ...init, signal: controller.signal }), controller, consumers: 0 };
    pending.set(key, entry);
  }
  const epoch = generation;
  entry.consumers++;
  let cancel: (() => void) | undefined;
  try {
    const cancelled = new Promise<never>((_, reject) => {
      cancel = () => reject(init?.signal?.reason || new DOMException('Aborted', 'AbortError'));
      init?.signal?.addEventListener('abort', cancel, { once: true });
    });
    const response = await Promise.race([entry.promise, cancelled]);
    if (epoch !== generation) throw new DOMException('Search session ended', 'AbortError');
    init?.signal?.throwIfAborted();
    return response.clone();
  } finally {
    if (cancel) init?.signal?.removeEventListener('abort', cancel);
    entry.consumers--;
    if (entry.consumers === 0) {
      entry.controller.abort();
      if (pending.get(key) === entry) pending.delete(key);
    }
  }
}
